import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControlLabel, IconButton, LinearProgress, MenuItem, Switch, TextField,
    ToggleButton, Tooltip, Typography, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import { VLearnTypeIcon, VLEARN_TYPE_META, vlearnInputSx, vlearnType } from "./vlearnUi";
import { confirmDialog } from "../popups/ConfirmDialog";
import {
    VLEARN_LESSON_TYPES, deleteVLearnFile, saveVLearnLesson, uploadVLearnFile
} from "../../containers/vlearn/vlearnSlice";

/**
 * V-Learning — the lesson editor.
 *
 * ── Why the lesson is saved before a file can be attached ────────────────────────────
 *
 * An upload goes to `/v1/vlearn/files/upload?id_lesson=..&kind=..`, and that `id_lesson` is
 * what the backend authorises against BEFORE multer writes a byte to disk. There is
 * therefore no such thing as attaching a file to a lesson that does not exist yet. The
 * dialog makes that ordering visible instead of hiding it: for a new file-backed lesson the
 * uploader is replaced by a line saying the lesson has to be created first, and it appears
 * the moment the save returns an id. The alternative — buffering the file in the browser and
 * posting it after the save — would mean holding a 400 MB video in memory and inventing a
 * second failure mode for the upload.
 *
 * ── Why a slides lesson has two uploaders ────────────────────────────────────────────
 *
 * No browser renders a .pptx, and this platform has no conversion service. So a slides
 * lesson keeps both: the PDF export is what learners actually see inline, and the .pptx is
 * kept as the downloadable editable source so the original is not lost. That is the designed
 * behaviour rather than an omission, which is why the helper text says so.
 */

/** The 11-character YouTube id, anchored — see `parseYoutubeId` in the backend's helpers. */
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/

const YOUTUBE_HOSTS = ['youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'youtu.be']

/**
 * The same parser the backend applies, in the same order.
 *
 * Duplicated on purpose. The server is the guarantee — it stores the id, never the URL, so
 * an embed can never be pointed at another origin. This copy exists only so the author sees
 * "that is not a YouTube link" while the field is still focused, and so the thumbnail below
 * can be drawn without a round trip.
 */
function parseYoutubeId(input) {
    if (!input || typeof input !== 'string') return null
    const value = input.trim()
    if (!value) return null
    if (YOUTUBE_ID_RE.test(value)) return value

    let url
    try {
        // A paste without a scheme ("youtu.be/xyz") is the common case and the URL parser
        // refuses it, so it is given one. Only the host and path are ever read from it.
        url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`)
    } catch (error) {
        return null
    }

    const host = url.hostname.toLowerCase().replace(/^www\./, '')
    if (!YOUTUBE_HOSTS.includes(host)) return null

    if (host === 'youtu.be') {
        const candidate = url.pathname.split('/').filter(Boolean)[0]
        return candidate && YOUTUBE_ID_RE.test(candidate) ? candidate : null
    }

    const v = url.searchParams.get('v')
    if (v && YOUTUBE_ID_RE.test(v)) return v

    const parts = url.pathname.split('/').filter(Boolean)
    if (parts.length >= 2 && ['embed', 'shorts', 'live', 'v'].includes(parts[0])) {
        return YOUTUBE_ID_RE.test(parts[1]) ? parts[1] : null
    }
    return null
}

/** http and https only, matching `sanitizeExternalUrl`. The value becomes a learner's href. */
const isSafeExternalUrl = (value) => {
    if (!value) return false
    try {
        const url = new URL(String(value).trim())
        return url.protocol === 'http:' || url.protocol === 'https:'
    } catch (error) {
        return false
    }
}

/**
 * The uploaders each lesson type offers, and the `kind` each one posts.
 *
 * Mirrors `LESSON_FILE_KINDS` in the backend's helpers, which refuses any other pairing.
 * `accept` is narrowed to the mime types `VLearnFilesController` allows for that kind: the
 * `document` kind takes a .docx and nothing else, because a PDF offered as `document` is
 * refused there and would be a control that cannot succeed.
 */
const UPLOADERS = {
    video_upload: [
        {
            kind: 'video', accept: 'video/mp4,video/webm', video: true,
            labelKey: 'VLEARN_UPLOAD_VIDEO', helpKey: 'VLEARN_UPLOAD_VIDEO_HELP'
        },
        {
            kind: 'caption', accept: 'text/vtt,.vtt', optional: true,
            labelKey: 'VLEARN_UPLOAD_CAPTION', helpKey: 'VLEARN_UPLOAD_CAPTION_HELP'
        }
    ],
    pdf: [
        {
            kind: 'pdf', accept: 'application/pdf',
            labelKey: 'VLEARN_UPLOAD_PDF', helpKey: 'VLEARN_UPLOAD_PDF_HELP'
        }
    ],
    slides: [
        {
            kind: 'pdf', accept: 'application/pdf',
            labelKey: 'VLEARN_UPLOAD_SLIDES_PDF', helpKey: 'VLEARN_UPLOAD_SLIDES_PDF_HELP'
        },
        {
            kind: 'slides_source',
            accept: '.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation',
            optional: true,
            labelKey: 'VLEARN_UPLOAD_SLIDES_SOURCE', helpKey: 'VLEARN_UPLOAD_SLIDES_SOURCE_HELP'
        }
    ],
    document: [
        {
            kind: 'document',
            accept: '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            labelKey: 'VLEARN_UPLOAD_DOCUMENT', helpKey: 'VLEARN_UPLOAD_DOCUMENT_HELP'
        }
    ]
}

/** See the identical note in CourseEditor: two thunk families, two error shapes. */
const serverMessage = (payload) => {
    const error = payload && payload.responseError
    if (!error) return null
    const data = error.data || (error.response && error.response.data)
    return (data && data.error) || null
}

const formatBytes = (bytes) => {
    const size = Number(bytes) || 0
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const emptyForm = {
    title: '', description: '', type: 'text',
    is_mandatory: true,
    duration_seconds: '', min_seconds: '',
    youtube_url: '', external_url: '', content_text: '', id_quiz: ''
}

/** The stored lesson reduced to the fields this dialog owns. */
const fromLesson = (lesson) => ({
    title: lesson.title || '',
    description: lesson.description || '',
    type: lesson.type || 'text',
    is_mandatory: !!Number(lesson.is_mandatory),
    duration_seconds: lesson.duration_seconds || '',
    min_seconds: lesson.min_seconds || '',
    // Rebuilt from the stored id so the field round-trips: the server keeps the id, never
    // the URL the author pasted.
    youtube_url: lesson.youtube_id ? `https://www.youtube.com/watch?v=${lesson.youtube_id}` : '',
    external_url: lesson.external_url || '',
    content_text: lesson.content_text || '',
    id_quiz: lesson.id_quiz || ''
})

export default function LessonEditorDialog({ open, lesson, idSection, idCourse, onClose, onSaved }) {

    const dispatch = useDispatch()

    const allowedLessonTypes = useSelector((state) => state.vlearn.allowedLessonTypes)
    const settings = useSelector((state) => state.vlearn.settings)
    const quizzes = useSelector((state) => state.vlearn.quizzes)
    const sections = useSelector((state) => state.vlearn.sections)
    const uploadProgress = useSelector((state) => state.vlearn.uploadProgress)
    const lang = useSelector((state) => state.settings.lang)

    const [form, setForm] = useState(emptyForm)
    const [saving, setSaving] = useState(false)
    const [uploadingKind, setUploadingKind] = useState(null)
    const [errorKey, setErrorKey] = useState(null)
    /** The id of a lesson this dialog created, so the uploaders can appear without closing. */
    const [createdId, setCreatedId] = useState(null)

    const fileInputs = useRef({})

    const idLesson = lesson ? lesson.id : createdId

    // Reset whenever the dialog opens on a different lesson — including on "new", where the
    // previous lesson's values must not survive into a blank form.
    useEffect(() => {
        if (!open) return
        setErrorKey(null)
        setCreatedId(null)
        setForm(lesson ? fromLesson(lesson) : {
            ...emptyForm,
            // The first allowed type rather than a hardcoded one: `video_youtube` may be
            // switched off by the module's `video_source` setting.
            type: (allowedLessonTypes || VLEARN_LESSON_TYPES)[0] || 'text'
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, lesson && lesson.id])

    /**
     * The lesson as the store currently holds it.
     *
     * Read here rather than taken from the `lesson` prop because an upload is followed by a
     * refetch, and for a lesson this dialog has just created the parent is still passing
     * null — the freshly attached file would otherwise not appear until the dialog closed.
     */
    const storedLesson = useMemo(() => {
        if (!idLesson) return null
        for (const section of (sections || [])) {
            const found = (section.lessons || []).find(l => l.id === idLesson)
            if (found) return found
        }
        return lesson || null
    }, [idLesson, sections, lesson])

    const files = (storedLesson && storedLesson.files) || []

    const youtubeId = useMemo(
        () => (form.type === 'video_youtube' ? parseYoutubeId(form.youtube_url) : null),
        [form.type, form.youtube_url])

    const edit = (changes) => {
        setErrorKey(null)
        setForm(prev => ({ ...prev, ...changes }))
    }

    /**
     * The same checks `saveLesson` makes, so the author is told which field is wrong instead
     * of reading a 422 that names a body key. The server still re-checks every one of them.
     */
    const validate = useCallback(() => {
        if (!String(form.title || '').trim()) return 'VLEARN_LESSON_TITLE_REQUIRED'
        if (form.type === 'video_youtube' && !parseYoutubeId(form.youtube_url)) {
            return 'VLEARN_YOUTUBE_INVALID'
        }
        if (form.type === 'link' && !isSafeExternalUrl(form.external_url)) {
            return 'VLEARN_LINK_INVALID'
        }
        if (form.type === 'text' && !String(form.content_text || '').trim()) {
            return 'VLEARN_TEXT_REQUIRED'
        }
        if (form.type === 'quiz' && !form.id_quiz) return 'VLEARN_QUIZ_REQUIRED'
        return null
    }, [form])

    const onSave = async () => {
        const problem = validate()
        if (problem) return setErrorKey(problem)

        const body = {
            id_course: idCourse,
            id_section: idSection,
            title: String(form.title).trim(),
            type: form.type,
            description: form.description,
            is_mandatory: form.is_mandatory,
            duration_seconds: form.duration_seconds,
            min_seconds: form.min_seconds
        }
        if (idLesson) body.id_lesson = idLesson
        if (form.type === 'video_youtube') body.youtube_url = form.youtube_url
        if (form.type === 'link') body.external_url = form.external_url
        if (form.type === 'text') body.content_text = form.content_text
        if (form.type === 'quiz') body.id_quiz = form.id_quiz

        setSaving(true)
        const response = await dispatch(saveVLearnLesson(body))
        setSaving(false)
        const data = response.payload && response.payload.responseData
        if (!data || data.status !== 1) {
            toast.error(serverMessage(response.payload) || Localize("VLEARN_ACTION_FAILED", lang))
            return
        }
        toast.success(Localize("VLEARN_LESSON_SAVED", lang))
        if (!idLesson) setCreatedId(data.id)
        if (onSaved) onSaved()

        // A lesson that needs no file is finished the moment it is saved. One that does stays
        // open, because the uploader only becomes usable now that there is an id to attach to.
        if (!UPLOADERS[form.type]) onClose()
    }

    const onPickFile = async (uploader, event) => {
        const file = event.target.files && event.target.files[0]
        // Cleared straight away so choosing the same file twice still fires a change event.
        event.target.value = ''
        if (!file || !idLesson) return

        // The cap the server will apply, applied here first: rejecting a 900 MB video after
        // it has been uploaded wastes the author's afternoon.
        const maxMb = uploader.video
            ? Number(settings.max_video_upload_mb) || 500
            : Number(settings.max_document_upload_mb) || 50
        if (file.size > maxMb * 1000 * 1000) {
            toast.error(Localize("VLEARN_FILE_TOO_LARGE", lang).replace('%max%', maxMb))
            return
        }

        setUploadingKind(uploader.kind)
        const response = await dispatch(uploadVLearnFile({
            file, idLesson, kind: uploader.kind
        }))
        setUploadingKind(null)
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            toast.success(Localize("VLEARN_UPLOADED", lang))
            if (onSaved) onSaved()
        } else {
            toast.error(serverMessage(response.payload) || Localize("VLEARN_UPLOAD_FAILED", lang))
        }
    }

    const onDeleteFile = (file) => {
        confirmDialog({
            title: <Langs str="VLEARN_DELETE_FILE" />,
            message: <Langs str="VLEARN_DELETE_FILE_CONFIRM_BODY" params={{ NAME: file.original_name }} />,
            danger: true,
            confirmLabel: <Langs str="VLEARN_DELETE" />,
            onConfirm: () => deleteFile(file),
        })
    }

    const deleteFile = async (file) => {
        const response = await dispatch(deleteVLearnFile({ id: file.id }))
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            toast.success(Localize("VLEARN_FILE_DELETED", lang))
            if (onSaved) onSaved()
        } else {
            toast.error(serverMessage(response.payload) || Localize("VLEARN_ACTION_FAILED", lang))
        }
    }


    // 16px below `sm`: iOS Safari zooms the whole page when a focused input's
    // computed font-size is under 16px, and it does not zoom back out.
    const inputSx = vlearnInputSx

    /*
     * `isMobile === 2` is the platform's phone flag; the media query covers a narrowed
     * desktop window. Either one makes this dialog full-screen — see the `fullScreen` prop.
     */
    const isMobileRedux = useSelector((state) => state.settings.isMobile)
    const theme = useTheme()
    const isSmallQuery = useMediaQuery(theme.breakpoints.down('sm'))
    const isSmall = isMobileRedux === 2 || isSmallQuery
    const labelProps = { sx: { fontSize: '0.86rem' } }

    /** One uploader: its own file input, its own progress bar, its own existing files. */
    const renderUploader = (uploader) => {
        const own = files.filter(file => file.kind === uploader.kind)
        const busy = uploadingKind === uploader.kind
        return (
            <Box key={uploader.kind} sx={{
                padding: '12px', borderRadius: '10px',
                border: '1px dashed rgba(0,0,0,0.16)'
            }}>
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 600 }}>
                    <Langs str={uploader.labelKey} />
                    {uploader.optional ? (
                        <Box component="span" sx={{ ...vlearnType.meta, ml: 0.75 }}>
                            <Langs str="VLEARN_OPTIONAL" />
                        </Box>
                    ) : null}
                </Typography>
                <Typography sx={{ ...vlearnType.meta, mt: 0.25 }}>
                    <Langs str={uploader.helpKey} />
                </Typography>

                <input
                    type="file" accept={uploader.accept}
                    ref={(node) => { fileInputs.current[uploader.kind] = node }}
                    onChange={(event) => onPickFile(uploader, event)}
                    style={{ display: 'none' }} aria-hidden="true" tabIndex={-1}
                />
                <Button size="small" variant="outlined" disabled={busy}
                    startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                        const node = fileInputs.current[uploader.kind]
                        if (node) node.click()
                    }}
                    sx={{ textTransform: 'none', mt: 1.25 }}>
                    <Langs str="VLEARN_CHOOSE_FILE" />
                </Button>
                <Typography component="span" sx={{ ...vlearnType.meta, ml: 1 }}>
                    <Langs str="VLEARN_MAX_SIZE" params={{
                        max: uploader.video
                            ? (settings.max_video_upload_mb || 500)
                            : (settings.max_document_upload_mb || 50)
                    }} />
                </Typography>

                {busy && uploadProgress !== null ? (
                    <Box sx={{ mt: 1.25 }}>
                        <LinearProgress variant="determinate" value={uploadProgress}
                            sx={{ height: 6, borderRadius: 3 }} />
                        <Typography sx={{ ...vlearnType.meta, mt: 0.5 }}>{uploadProgress}%</Typography>
                    </Box>
                ) : null}

                {own.length ? (
                    <Box component="ul" sx={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
                        {own.map(file => (
                            <Box component="li" key={file.id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                padding: '4px 0'
                            }}>
                                <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                                    <Typography sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                        {file.original_name}
                                    </Typography>
                                    <Typography sx={vlearnType.meta}>{formatBytes(file.size)}</Typography>
                                </Box>
                                <Tooltip arrow title={Localize("VLEARN_DELETE_FILE", lang)}>
                                    <IconButton size="small" onClick={() => onDeleteFile(file)}
                                        aria-label={Localize("VLEARN_DELETE_FILE", lang)}>
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                ) : null}
            </Box>
        )
    }

    const uploaders = UPLOADERS[form.type]

    return (
        <Dialog open={open} onClose={(event, reason) => {
                // A form holding typed input must not be dismissed by a stray click on the
                // backdrop or a reflex Escape: that discarded everything, with no warning and
                // no undo. Closing is an explicit choice now — Cancel, or the save button.
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
                onClose()
            }} fullWidth maxWidth="sm"
            /*
             * FULL-SCREEN ON A PHONE.
             *
             * This is the module's biggest form — a type switcher, a title, a description, an
             * upload slot, duration and mandatory flags — and as a floating paper it was a
             * dialog taller than the viewport with 32px of unusable backdrop either side, so
             * the fields were narrower than the screen that could not hold them. It also
             * refuses backdrop and Escape dismissal by design, which makes the shrunken
             * DialogActions the ONLY way out; those were below the fold.
             *
             * Full-screen gives the fields the whole width and pins the actions to a real
             * bottom edge. `PaperProps` adds the safe-area insets, which a full-screen paper
             * needs and a floating one does not.
             */
            fullScreen={isSmall}
            PaperProps={isSmall ? {
                sx: {
                    paddingTop: 'env(safe-area-inset-top, 0px)',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)'
                }
            } : undefined}
            aria-labelledby="vlearn-lesson-dialog-title">
            <DialogTitle id="vlearn-lesson-dialog-title"
                sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
                <Langs str={idLesson ? 'VLEARN_EDIT_LESSON' : 'VLEARN_NEW_LESSON'} />
            </DialogTitle>

            <DialogContent dividers>
                {/* At the top, not the bottom: this dialog is taller than the viewport and
                    does not scroll to its own error, so a rejected save looked like a button
                    that did nothing until you scrolled down to find out why. */}
                {errorKey ? (
                    <Alert severity="error" sx={{ fontSize: '0.82rem', mb: 2 }}>
                        <Langs str={errorKey} />
                    </Alert>
                ) : null}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* ── Type ───────────────────────────────────────────────
                        Every type is drawn, including the ones the module's `video_source`
                        setting currently forbids. Hiding them would leave an author who
                        remembers uploading a video yesterday with no explanation for why the
                        option has gone; a disabled button that names the setting gives them
                        something to ask an administrator about. */}
                    <Box>
                        <Typography component="h3" sx={{ ...vlearnType.sectionTitle, mb: 0.75 }}>
                            <Langs str="VLEARN_LESSON_TYPE" />
                        </Typography>
                        {/* Individual ToggleButtons rather than a ToggleButtonGroup: a
                            disabled one has to be wrapped in a span to carry its Tooltip,
                            and a group only wires up its direct children. */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {VLEARN_LESSON_TYPES.map(type => {
                                const meta = VLEARN_TYPE_META[type]
                                const allowed = (allowedLessonTypes || []).includes(type)
                                const button = (
                                    <ToggleButton
                                        value={type} size="small" disabled={!allowed}
                                        selected={form.type === type}
                                        onChange={() => edit({ type })}
                                        sx={{
                                            border: '1px solid rgba(0,0,0,0.12)',
                                            borderRadius: '8px', textTransform: 'none',
                                            fontSize: '0.78rem', gap: '6px', paddingY: '5px',
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(255, 144, 21, 0.14)',
                                                borderColor: 'rgba(255, 144, 21, 0.45)'
                                            },
                                            '&:focus-visible': {
                                                outline: '2px solid var(--orange, rgb(255,144,21))',
                                                outlineOffset: '1px'
                                            }
                                        }}
                                    >
                                        <VLearnTypeIcon type={type} />
                                        <Langs str={meta.key} />
                                    </ToggleButton>
                                )
                                if (allowed) return <Box key={type} sx={{ display: 'inline-flex' }}>{button}</Box>
                                return (
                                    <Tooltip key={type} arrow
                                        title={Localize("VLEARN_TYPE_DISABLED", lang)
                                            .replace('%setting%', 'video_source')
                                            .replace('%value%', settings.video_source || 'both')}>
                                        <span>{button}</span>
                                    </Tooltip>
                                )
                            })}
                        </Box>
                    </Box>

                    <Divider />

                    {/* ── Common fields ──────────────────────────────────── */}
                    <TextField
                        size="small" fullWidth required autoFocus
                        label={<Langs str="VLEARN_FIELD_TITLE" />}
                        value={form.title}
                        inputProps={{ maxLength: 190 }}
                        onChange={(event) => edit({ title: event.target.value })}
                        sx={inputSx} InputLabelProps={labelProps}
                    />
                    <TextField
                        size="small" fullWidth multiline minRows={2}
                        label={<Langs str="VLEARN_FIELD_DESCRIPTION" />}
                        value={form.description}
                        onChange={(event) => edit({ description: event.target.value })}
                        helperText={<Langs str="VLEARN_LESSON_DESCRIPTION_HELP" />}
                        sx={inputSx} InputLabelProps={labelProps}
                    />

                    {/* ── Per-type content ───────────────────────────────── */}
                    {form.type === 'video_youtube' ? (
                        <Box>
                            <TextField
                                size="small" fullWidth
                                label={<Langs str="VLEARN_FIELD_YOUTUBE_URL" />}
                                value={form.youtube_url}
                                onChange={(event) => edit({ youtube_url: event.target.value })}
                                error={!!form.youtube_url && !youtubeId}
                                helperText={form.youtube_url && !youtubeId
                                    ? <Langs str="VLEARN_YOUTUBE_INVALID" />
                                    : <Langs str="VLEARN_FIELD_YOUTUBE_URL_HELP" />}
                                sx={inputSx} InputLabelProps={labelProps}
                            />
                            {/* The thumbnail is the confirmation that matters: an author who
                                pasted the wrong link recognises the wrong picture instantly,
                                where they would not notice a wrong id. */}
                            {youtubeId ? (
                                <Box sx={{ mt: 1.25 }}>
                                    <img
                                        src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
                                        alt={Localize("VLEARN_YOUTUBE_PREVIEW_ALT", lang)}
                                        style={{
                                            width: 220, maxWidth: '100%', borderRadius: 10,
                                            border: '1px solid rgba(0,0,0,0.08)'
                                        }}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}

                    {form.type === 'link' ? (
                        <TextField
                            size="small" fullWidth
                            label={<Langs str="VLEARN_FIELD_EXTERNAL_URL" />}
                            value={form.external_url}
                            onChange={(event) => edit({ external_url: event.target.value })}
                            error={!!form.external_url && !isSafeExternalUrl(form.external_url)}
                            helperText={<Langs str="VLEARN_FIELD_EXTERNAL_URL_HELP" />}
                            sx={inputSx} InputLabelProps={labelProps}
                        />
                    ) : null}

                    {form.type === 'text' ? (
                        <TextField
                            size="small" fullWidth multiline minRows={6}
                            label={<Langs str="VLEARN_FIELD_CONTENT_TEXT" />}
                            value={form.content_text}
                            onChange={(event) => edit({ content_text: event.target.value })}
                            helperText={<Langs str="VLEARN_FIELD_CONTENT_TEXT_HELP" />}
                            sx={inputSx} InputLabelProps={labelProps}
                        />
                    ) : null}

                    {form.type === 'quiz' ? (
                        <Box>
                            <TextField
                                select size="small" fullWidth
                                label={<Langs str="VLEARN_FIELD_QUIZ" />}
                                value={form.id_quiz}
                                onChange={(event) => edit({ id_quiz: event.target.value })}
                                helperText={<Langs str="VLEARN_FIELD_QUIZ_HELP" />}
                                sx={inputSx} InputLabelProps={labelProps}
                            >
                                {(quizzes || []).length ? null : (
                                    <MenuItem value="" disabled>
                                        <Langs str="VLEARN_NO_QUIZ_YET" />
                                    </MenuItem>
                                )}
                                {(quizzes || []).map(quiz => (
                                    <MenuItem key={quiz.id} value={quiz.id}>{quiz.title}</MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    ) : null}

                    {/* ── Files ──────────────────────────────────────────── */}
                    {uploaders ? (
                        idLesson ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {uploaders.map(renderUploader)}
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ fontSize: '0.82rem' }}>
                                <Langs str="VLEARN_SAVE_BEFORE_UPLOAD" />
                            </Alert>
                        )
                    ) : null}

                    <Divider />

                    {/* ── Rules ─────────────────────────────────────────── */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: '12px'
                    }}>
                        <Box>
                            <FormControlLabel
                                control={<Switch size="small" checked={form.is_mandatory}
                                    onChange={(event) => edit({ is_mandatory: event.target.checked })} />}
                                label={<Typography sx={{ fontSize: '0.86rem' }}>
                                    <Langs str="VLEARN_FIELD_LESSON_MANDATORY" />
                                </Typography>}
                            />
                            <Typography sx={{ ...vlearnType.meta, ml: 5.5, mt: -0.5 }}>
                                <Langs str="VLEARN_FIELD_LESSON_MANDATORY_HELP" />
                            </Typography>
                        </Box>
                        {/*
                          * There was a "Free preview" switch here. Removed: it promised
                          * "openable without being enrolled" and NOTHING enforced it — no
                          * access check ever read `is_preview`, so the control did nothing.
                          *
                          * More importantly the concept does not apply here. Free preview is
                          * public-marketplace design (Udemy, Coursera): a stranger deciding
                          * whether to BUY a course gets to sample a lesson. V-Learning is
                          * internal — enrolment is not a choice, courses are assigned by an
                          * audience rule, there is no self-enrolment, and a learner only ever
                          * sees courses they are already on. Being added IS being enrolled.
                          *
                          * The one real need it looked like it served — an author or manager
                          * wanting to look without enrolling — is served properly by preview
                          * mode in `VLearnProgressController.openLesson`, which is a
                          * permission rather than a per-lesson flag.
                          */}

                        <TextField
                            size="small" fullWidth type="number"
                            label={<Langs str="VLEARN_FIELD_DURATION_SECONDS" />}
                            value={form.duration_seconds}
                            inputProps={{ min: 0, step: 30 }}
                            onChange={(event) => edit({ duration_seconds: event.target.value })}
                            helperText={<Langs str="VLEARN_FIELD_DURATION_SECONDS_HELP" />}
                            sx={inputSx} InputLabelProps={labelProps}
                        />
                        <TextField
                            size="small" fullWidth type="number"
                            label={<Langs str="VLEARN_FIELD_MIN_SECONDS" />}
                            value={form.min_seconds}
                            inputProps={{ min: 0, step: 30 }}
                            onChange={(event) => edit({ min_seconds: event.target.value })}
                            helperText={<Langs str="VLEARN_FIELD_MIN_SECONDS_HELP" />}
                            sx={inputSx} InputLabelProps={labelProps}
                        />
                    </Box>

                </Box>
            </DialogContent>

            {/* `gap` and a 44px floor: Close discards a half-typed lesson and Save keeps it,
                and MUI's default 8px between them is not enough separation for a thumb. */}
            <DialogActions sx={{ gap: 1, px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', minHeight: 44 }}>
                    <Langs str={idLesson ? 'VLEARN_CLOSE' : 'VLEARN_CANCEL'} />
                </Button>
                <Button onClick={onSave} variant="contained" disabled={saving}
                    sx={{
                        textTransform: 'none', minHeight: 44,
                        backgroundColor: 'var(--orange, rgb(255,144,21))',
                        '&:hover': { backgroundColor: '#e07f0d' }
                    }}>
                    <Langs str={idLesson ? 'VLEARN_SAVE' : 'VLEARN_CREATE_LESSON'} />
                </Button>
            </DialogActions>
        </Dialog>
    )
}
